import type { ListContext } from '@platewell/query';
import { Link } from '@tanstack/react-router';

/**
 * The route a list context points back at.
 *
 * Shared by the link and the keyboard shortcut so the two can never send the user to
 * different places.
 */
export function backToListTarget(context: ListContext) {
  switch (context.kind) {
    case 'collection':
      return {
        to: '/collections/$collectionId',
        params: { collectionId: context.collectionId },
      } as const;
    case 'favourites':
      return { to: '/favourites', search: {} } as const;
    case 'recent':
      return { to: '/recent', search: {} } as const;
    case 'tag':
      return {
        to: '/',
        search: { tag: context.tag, q: context.term === '' ? undefined : context.term },
      } as const;
    case 'search':
      return { to: '/', search: { q: context.term } } as const;
    case 'all':
      return { to: '/', search: {} } as const;
  }
}

/** A breadcrumb back to the collection, tag, search, or list the recipe was opened from. */
export function BackToListLink({
  context,
  label,
  className = 'result-pager-arrow result-pager-back',
}: {
  context: ListContext;
  label: string;
  className?: string;
}) {
  const target = backToListTarget(context);
  return (
    <Link className={className} {...target} aria-label={`Back to ${label}`}>
      ‹ {label}
    </Link>
  );
}
